import { MessageBar, MessageBarType } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import {
    ConfirmDialog,
    createListTableColumns,
    depascalize,
    ensure,
    ShimmeredDetailsList,
    useNotifications,
    ValidationResult,
    ActionButton,
    ValidationFailure,
    Badge,
    BadgeColor
} from "sprint-common-web";

import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { getStatusName } from "../../common/utils/getStatusName";
import { OrderListingContext } from "../context/OrderListingContext";
import { CloudOrderSource, OrderListingItem } from "../OrderListingItem";
import { MergeOrdersItem, mapToMergeOrderItem, MergeDirection } from "./MergeOrdersItem";
import { getMergeOrdersValidationResult, MergeOrdersValidationRequest } from "./MergeOrdersValidation";

interface ApproveOrderDeliveriesDialogProps {
    isOpen: boolean;
    onClose(): void;
}

export const MergeOrdersDialog: React.VFC<ApproveOrderDeliveriesDialogProps> = ({ isOpen, onClose }) => {
    const { mergeOrders } = useOrdersModuleApi();

    const { mergeListItems, updateOrder, removeItemsFromMergeList, selectedItems } = useContext(OrderListingContext);
    const [sourceItem, setSourceItem] = useState<MergeOrdersItem>({} as MergeOrdersItem);
    const [targetItem, setTargetItem] = useState<MergeOrdersItem>({} as MergeOrdersItem);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const { success, error } = useNotifications();

    useEffect(() => {
        if (mergeListItems.length === 2) {
            setMergeItems(mergeListItems);

            return;
        }

        if (selectedItems.length === 2) {
            setMergeItems(selectedItems);
        }
    }, [mergeListItems.length, selectedItems.length]);

    useEffect(() => {
        const validationRequest: MergeOrdersValidationRequest = {
            sourceOrder: sourceItem,
            targetOrder: targetItem
        };

        const result = getMergeOrdersValidationResult(validationRequest);

        setValidationResult(result);
    }, [sourceItem]);

    const setMergeItems = (items: OrderListingItem[]) => {
        const sourceOrder = mapToMergeOrderItem(items[0], MergeDirection.Source);
        const targetOrder = mapToMergeOrderItem(items[1], MergeDirection.Target);

        setSourceItem(sourceOrder);
        setTargetItem(targetOrder);
    };

    const handleMerge = async (): Promise<void> => {
        try {
            await mergeOrders({
                sourceOrderId: sourceItem.id,
                targetOrderId: targetItem.id
            });

            mergeInContextAndClose();

            success("Selected orders merged");
        } catch {
            error("Merging orders failes");
        }
    };

    const mergeInContextAndClose = () => {
        updateOrder({ id: targetItem.id, status: OrderStatus.Overridden, isModified: true });
        updateOrder({ id: sourceItem.id, buzzId: targetItem.buzzId, isModified: true });

        removeItemsFromMergeList([targetItem.id, sourceItem.id]);

        handleClose();
    };

    const swapMergeDirection = () => {
        const newSourceItem = targetItem;
        const newTargetItem = sourceItem;

        newSourceItem.mergeDirection = MergeDirection.Source;
        newTargetItem.mergeDirection = MergeDirection.Target;

        setTargetItem(newTargetItem);
        setSourceItem(newSourceItem);
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <ConfirmDialog
            title={"Merge orders"}
            text={"Are you sure you want to merge given orders?"}
            hidden={!isOpen}
            confirm={{
                onClick: handleMerge,
                disabled: validationResult?.failures.length !== 0
            }}
            cancel={{ onClick: onClose }}
            width={"50%"}
            contentHeight={400}
        >
            <MergeOrdersSummary
                items={[sourceItem, targetItem]}
                onButtonClick={swapMergeDirection}
                failures={validationResult?.failures ?? []}
            />
        </ConfirmDialog>
    );
};

interface MergeOrdersSummaryProps {
    items: MergeOrdersItem[];
    failures: ValidationFailure[];
    onButtonClick(): void;
}

const MergeOrdersSummary: React.VFC<MergeOrdersSummaryProps> = ({
    items,
    onButtonClick,
    failures
}: MergeOrdersSummaryProps) => {
    return (
        <div>
            {MergeValidationSummary(failures)}
            <div
                style={{
                    justifyContent: "right",
                    alignItems: "right",
                    display: "flex",
                    marginTop: "2%"
                }}
            >
                <ActionButton
                    iconProps={{ iconName: "SwitcherStartEnd" }}
                    styles={{ icon: { fontSize: 20 } }}
                    ariaLabel="Swap statuses"
                    label="Swap"
                    onClick={onButtonClick}
                >
                    Swap
                </ActionButton>
            </div>
            <ShimmeredDetailsList columns={columns} items={items} />
        </div>
    );
};

const MergeValidationSummary = (failures: ValidationFailure[]) => {
    return (
        <>
            {failures.map((item: ValidationFailure) => (
                <MessageBar key={item.code} messageBarType={MessageBarType.error}>
                    <>
                        {item.message}
                        <br />
                    </>
                </MessageBar>
            ))}
        </>
    );
};

const renderOrderStatus = (item?: MergeOrdersItem) => {
    ensure(item);
    const status = getStatusName(item.status) + " ";

    return <>{status}</>;
};

const renderOrderSource = (item?: MergeOrdersItem) => {
    ensure(item);
    const origin = item.isImported ? "Imported" : depascalize(CloudOrderSource[item.source]);

    const color = getOriginBadgeColor(item.source, item.isImported);

    return <Badge text={origin} color={color} />;
};

const getOriginBadgeColor = (origin: CloudOrderSource, isImported?: boolean): BadgeColor => {
    if (isImported) {
        return BadgeColor.Yellow;
    }

    switch (origin) {
        case CloudOrderSource.Buzz:
            return BadgeColor.Gray;

        case CloudOrderSource.Shreq:
            return BadgeColor.Green;

        case CloudOrderSource.Sprint:
            return BadgeColor.ApplicationPrimary;
        default:
            return BadgeColor.ApplicationPrimary;
    }
};

const renderMergeDirection = (item?: MergeOrdersItem) => {
    ensure(item);
    const status = depascalize(MergeDirection[item.mergeDirection]) + " ";

    return <>{status}</>;
};

const columns = createListTableColumns<MergeOrdersItem>([
    {
        type: "basic",
        key: "mergeDirection",
        name: "Merge direction",
        onRender: renderMergeDirection,
        minWidth: 30,
        maxWidth: 110
    },
    {
        type: "basic",
        key: "poNumber",
        name: "PO number",
        minWidth: 150,
        maxWidth: 150
    },
    {
        type: "basic",
        key: "content",
        name: "Content",
        minWidth: 200,
        maxWidth: 200
    },
    {
        type: "basic",
        key: "airline",
        name: "Airline",
        minWidth: 140,
        maxWidth: 150
    },
    {
        type: "basic",
        key: "status",
        name: "Status",
        onRender: renderOrderStatus,
        minWidth: 100,
        maxWidth: 100
    },
    {
        type: "basic",
        key: "source",
        onRender: renderOrderSource,
        name: "Origin",
        minWidth: 20,
        maxWidth: 20
    }
]);
